import React from "react";
import { Plus, Trash2, Sparkles, Crown, UserCog, UsersRound } from "lucide-react";
import ModalShell from "../ModalShell";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import { deptName, personName, employeeCode } from "../../utils/names";

const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const ManagersModal = ({ styles, t, language, theme, busy, state, setState, onClose, onSave }) => {
  const { dept, list, suggestion, members = [] } = state;
  const showSuggestion = suggestion && !list.some((m) => m.infinityCode === suggestion.code);
  const c = styles.colors;

  // Everyone we can put a name to: department members, the department's
  // current managers (they may sit outside the department) and the suggestion.
  const people = new Map();
  for (const m of members) people.set(String(m.infinityCode), { infinityCode: m.infinityCode, name: personName(m, language), code: employeeCode(m) });
  for (const m of dept.managers || []) {
    if (!people.has(String(m.infinityCode))) {
      people.set(String(m.infinityCode), { infinityCode: m.infinityCode, name: personName(m.employee, language), code: employeeCode(m.employee) || m.infinityCode });
    }
  }
  if (suggestion && !people.has(String(suggestion.code))) {
    people.set(String(suggestion.code), { infinityCode: suggestion.code, name: suggestion.name || "", code: suggestion.code });
  }
  const options = [...people.values()];
  const labelFor = (p) => (p.name ? `${p.name} · ${p.code || p.infinityCode}` : String(p.code || p.infinityCode));

  const setList = (next) => setState({ ...state, list: next });
  const updateRow = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    setList(next);
  };
  const assignedCount = list.filter((m) => String(m.infinityCode || "").trim()).length;

  const roleButton = (active, onClick, Icon, label) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "8px 12px",
        borderRadius: "8px",
        border: "none",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        background: active ? "#2e7cba" : "transparent",
        color: active ? "#ffffff" : c.muted,
        transition: "all 0.15s ease",
      }}
    >
      <Icon size={14} />
      {label}
    </button>
  );

  return (
    <ModalShell
      styles={styles}
      t={t}
      busy={busy}
      onClose={onClose}
      maxWidth={720}
      title={`${t.managersTitle} — ${deptName(dept, language)}`}
      subtitle={t.managersSubtitle}
      footer={
        <>
          <button type="button" style={styles.btnSecondary(busy)} disabled={busy} onClick={onClose}>{t.cancel}</button>
          <button type="button" className="dept-btn-primary" style={styles.btnPrimary(busy)} disabled={busy} onClick={onSave}>
            {t.save}
          </button>
        </>
      }
    >
      {showSuggestion && (
        <div style={{ ...styles.suggestionCard, padding: "14px 16px", marginBottom: "20px" }}>
          <div style={{ display: "flex", gap: "12px", alignItems: "center", minWidth: 0, flex: 1 }}>
            <span style={styles.managerAvatar(true)}>
              {suggestion.name ? initials(suggestion.name) : <Sparkles size={16} />}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "14px", fontWeight: 700, overflowWrap: "anywhere", display: "flex", alignItems: "center", gap: "6px" }}>
                <Sparkles size={14} style={{ color: c.chipText, flexShrink: 0 }} />
                {t.suggested}: {suggestion.name || suggestion.code}
              </div>
              <p style={{ ...styles.hint, margin: "2px 0 0" }}>{t.suggestedManagerHint}</p>
            </div>
          </div>
          <button
            type="button"
            className="dept-btn-primary"
            style={styles.btnPrimary()}
            onClick={() => setList([...list, { infinityCode: suggestion.code, role: "MANAGER" }])}
          >
            <Plus size={15} />{t.addManager}
          </button>
        </div>
      )}

      <div style={styles.managersHeader}>
        <span>{t.managers}</span>
        {assignedCount > 0 && <span style={styles.chip}>{t.managersAssigned(assignedCount)}</span>}
      </div>

      {!list.length && (
        <div style={styles.managersEmpty}>
          <UsersRound size={36} style={{ opacity: 0.4 }} />
          <div style={{ fontSize: "15px", fontWeight: 700, color: c.body }}>{t.noManagersTitle}</div>
          <p style={{ ...styles.hint, margin: 0 }}>{t.noManagersYet}</p>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {list.map((m, i) => {
          const person = people.get(String(m.infinityCode));
          return (
            <div key={i} style={styles.managerCard}>
              <span style={styles.managerAvatar(m.role === "MANAGER")}>
                {person?.name ? initials(person.name) : (m.role === "MANAGER" ? <Crown size={16} /> : <UserCog size={16} />)}
              </span>
              <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                <ReusableDropdown
                  items={options}
                  value={m.infinityCode}
                  onChange={(code) => updateRow(i, { infinityCode: String(code) })}
                  placeholder={t.selectEmployee}
                  getItemKey={(p) => p.infinityCode}
                  getItemLabel={labelFor}
                  theme={theme}
                  language={language}
                  allowCreate
                  onCreate={(term) => ({ infinityCode: term.trim(), name: "", code: term.trim() })}
                  createLabel={t.useCode}
                />
              </div>
              <div style={styles.roleToggle} role="group" aria-label={t.managers}>
                {roleButton(m.role === "MANAGER", () => updateRow(i, { role: "MANAGER" }), Crown, t.manager)}
                {roleButton(m.role === "DEPUTY", () => updateRow(i, { role: "DEPUTY" }), UserCog, t.deputy)}
              </div>
              <button
                type="button"
                className="dept-action-btn danger"
                style={styles.actionBtn("danger")}
                title={t.remove}
                aria-label={t.remove}
                onClick={() => setList(list.filter((_, j) => j !== i))}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="dept-add-row"
        style={styles.addRowBtn}
        onClick={() => setList([...list, { infinityCode: "", role: list.some((r) => r.role === "MANAGER") ? "DEPUTY" : "MANAGER" }])}
      >
        <Plus size={16} />{t.addManager}
      </button>
    </ModalShell>
  );
};

export default ManagersModal;
